import { isJsonObject, isNonEmptyString, type JsonObject, type JsonValue } from "./json";
import { isPrincipalRef } from "./addressing";
import type {
  AttestedFrameMessage,
  DropReport,
  FrameDescriptor,
  RelayMessage,
  SessionState,
  SocketDrop,
  TokenListing,
  WireTool,
} from "./protocol";
import { WIRE_PROTOCOL_VERSION } from "./protocol";

/**
 * Parsing, as the first half of the taxonomy principle.
 *
 *   A FAILURE AND AN EMPTY SUCCESS MUST NEVER SHARE A SHAPE.
 *
 * So these return a REASON, never `null`. A parser that answers `null` makes
 * "the peer sent nothing" and "the peer sent something we could not read"
 * identical at the call site, and the second one is a bug in somebody's code
 * that will otherwise be logged as an idle socket.
 */
export type ParseResult<T> =
  | { readonly ok: true; readonly message: T }
  | { readonly ok: false; readonly reason: string };

function fail<T>(reason: string): ParseResult<T> {
  return { ok: false, reason };
}

/** JSON.parse, with the throw turned into a value. */
function decode(raw: string): ParseResult<JsonObject> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    return fail(`not JSON: ${error instanceof Error ? error.message : "unknown"}`);
  }
  if (!isJsonObject(parsed)) return fail("not a JSON object");
  return { ok: true, message: parsed };
}

function tagOf(message: JsonObject): string | null {
  return typeof message.t === "string" ? message.t : null;
}

function versionOk(message: JsonObject): boolean {
  return message.v === WIRE_PROTOCOL_VERSION;
}

/**
 * The relay's session view, read in one place so the panel and the frame agree
 * about a session.
 *
 * `state` is validated against the closed set rather than taken as a string.
 * A misspelled state would otherwise reach a panel that renders the fallback
 * branch, which is a wrong answer about the agent's access wearing a working
 * panel's clothes (#10).
 */
function parseSessionFields(value: JsonObject): {
  state: SessionState;
  activeProjectId: string | null;
  controlProjectIds: readonly string[];
} | null {
  const state = value.state;
  if (state !== "no_session" && state !== "no_active_project" && state !== "live") return null;
  const active =
    typeof value.activeProjectId === "string" && value.activeProjectId.length > 0
      ? value.activeProjectId
      : null;
  // `live` with no project is incoherent: it is the state that says one IS the
  // target. Refused rather than forwarded, because the panel would read it as
  // "active elsewhere" and under-report exactly as #10 recorded.
  if (state === "live" && active === null) return null;
  const listed = value.controlProjectIds;
  if (!Array.isArray(listed)) return null;
  const controlProjectIds: string[] = [];
  for (const id of listed) {
    if (!isNonEmptyString(id)) return null;
    controlProjectIds.push(id);
  }
  return { state, activeProjectId: active, controlProjectIds };
}

/**
 * A welcome's `workspaces` or `projects` (#37).
 *
 * ABSENT is `null` — a relay that predates names — and is not a failure.
 * PRESENT is the relay's own construction from a verified token, so anything
 * malformed there is refused whole (`false`) rather than filtered: a filtered
 * list would turn a broken relay into "the token does not list this workspace",
 * which the panel prints as a warning the user has no way to act on.
 */
function parseListings(value: JsonValue | undefined): TokenListing[] | null | false {
  if (value === undefined || value === null) return null;
  if (!Array.isArray(value)) return false;
  const listings: TokenListing[] = [];
  for (const entry of value) {
    if (!isJsonObject(entry) || !isNonEmptyString(entry.id)) return false;
    const name = entry.name ?? null;
    if (name !== null && typeof name !== "string") return false;
    listings.push({ id: entry.id, name });
  }
  return listings;
}

/**
 * `docs/tool-surface.md`'s *Description hygiene*, applied at the one place app-authored text ENTERS
 * the system rather than at either place it leaves.
 *
 * The spec says "length-capped and stripped of control characters at the edge",
 * and until 2026-09-11 nothing did either — it was a rule written in the present
 * tense that had never been implemented. The dispatcher is what made it urgent:
 * app text used to leave only through `tools/list`, and now leaves through the
 * discovery result too. Capping at ingest means the two exits cannot drift, and
 * means no downstream module has to remember.
 *
 * **Control characters are replaced, not deleted.** Deleting them lets
 * `"read\u0000only"` become the single word `readonly`; a space keeps the
 * original token boundary visible. Newlines go with them — a newline is a
 * control character, the spec grants no carve-out, and a description that can
 * fake a line break is a description that can fake a message boundary in the
 * model's context.
 *
 * The cap is deliberately generous: it is a backstop against an app pasting a
 * document into the user's context, not a style rule about good descriptions.
 */
const MAX_DESCRIPTION_CHARS = 1024;

function hygienic(description: string): string {
  // biome-ignore lint/suspicious/noControlCharactersInRegex: matching them is the point
  const stripped = description.replace(/[\u0000-\u001F\u007F]/g, " ");
  return stripped.length <= MAX_DESCRIPTION_CHARS
    ? stripped
    : `${stripped.slice(0, MAX_DESCRIPTION_CHARS - 1)}\u2026`;
}

function parseTool(value: JsonValue): WireTool | null {
  if (!isJsonObject(value)) return null;
  if (!isNonEmptyString(value.name)) return null;
  if (typeof value.description !== "string") return null;
  if (!isJsonObject(value.inputSchema)) return null;
  const tool: WireTool = {
    name: value.name,
    description: hygienic(value.description),
    inputSchema: value.inputSchema,
    destructiveHint: value.destructiveHint === true,
    canAwaitUser: value.canAwaitUser === true,
    settlesBeforeResolving: value.settlesBeforeResolving !== false,
  };
  return tool;
}

function parseTools(value: JsonValue): readonly WireTool[] | null {
  if (!Array.isArray(value)) return null;
  const tools: WireTool[] = [];
  for (const entry of value) {
    const tool = parseTool(entry);
    if (tool === null) return null;
    tools.push(tool);
  }
  return tools;
}

/**
 * A frame descriptor as reported by an app, with `appOrigin` supplied by the
 * CALLER rather than read from the payload. Making it a parameter is the whole
 * point of this awkward signature: it is what stops an app claiming to be
 * another origin.
 *
 * ⚠️ **Module-private as of #22, and that is a demotion worth reading.** The
 * argument used to be the broker's `event.origin` — browser-attested, but on
 * the postMessage hop rather than the one that mattered. The broker is gone and
 * the sole caller is now `parseFrameMessage` below, which passes the WebSocket
 * handshake's `Origin` header straight through. That is attested by the browser
 * on the hop the relay actually terminates, which is what makes the field
 * trustworthy rather than merely named.
 *
 * Keeping it module-private is what keeps that true: an exported version could
 * be handed a value from anywhere, and the whole property is that this argument
 * has exactly one source.
 */
function parseFrameDescriptor(value: JsonObject, appOrigin: string | null): FrameDescriptor | null {
  if (!isNonEmptyString(value.frameId)) return null;
  if (!isNonEmptyString(value.app)) return null;
  if (!isNonEmptyString(value.workspaceId)) return null;
  if (!isNonEmptyString(value.projectId)) return null;
  if (typeof value.projectUrl !== "string") return null;
  const tools = parseTools(value.tools ?? []);
  if (tools === null) return null;
  return {
    frameId: value.frameId,
    app: value.app,
    appOrigin,
    workspaceId: value.workspaceId,
    projectId: value.projectId,
    projectUrl: value.projectUrl,
    tools,
  };
}

/**
 * A message from an app frame, with `attestedOrigin` supplied by the CALLER.
 *
 * ⚠️ That argument is the WebSocket handshake's `Origin` header and it is the
 * whole point of this signature. Until #22 the relay read `appOrigin` out of
 * the `frames` PAYLOAD — attacker-controlled text arriving over a socket
 * anyone may dial — while `protocol.ts` claimed the field was attested. It was,
 * on the app to broker `postMessage` hop, and then was not on the hop that
 * mattered: a non-browser client forged it from Node for a year.
 *
 * Null when the socket carried no `Origin` at all, which is every non-browser
 * client. Null is recorded as null: falling back to the payload would make the
 * attested field forgeable by simply omitting a header.
 */
export function parseFrameMessage(
  raw: string,
  attestedOrigin: string | null,
): ParseResult<AttestedFrameMessage> {
  const decoded = decode(raw);
  if (!decoded.ok) return decoded;
  const value = decoded.message;
  switch (tagOf(value)) {
    case "hello": {
      if (!versionOk(value)) return fail(`protocol version ${String(value.v)} is not supported`);
      // NOT normalised to null the way `install` was. An empty credential is not
      // a credential, and at v2 the null case had a lobby to land in; it has
      // nowhere to land now, so it is a refusal with a reason instead.
      if (!isNonEmptyString(value.token)) return fail("hello: token missing");
      return {
        ok: true,
        message: {
          t: "hello",
          v: WIRE_PROTOCOL_VERSION,
          token: value.token,
        },
      };
    }
    case "frames": {
      if (!Array.isArray(value.frames)) return fail("frames: frames missing");
      const frames: FrameDescriptor[] = [];
      for (const entry of value.frames) {
        if (!isJsonObject(entry)) return fail("frames: entry is not an object");
        // `entry.appOrigin` is DELIBERATELY not read. A frame may claim one;
        // the relay uses the handshake's and nothing else.
        const frame = parseFrameDescriptor(entry, attestedOrigin);
        if (frame === null) return fail("frames: entry malformed");
        frames.push(frame);
      }
      return { ok: true, message: { t: "frames", frames } };
    }
    case "front":
      return { ok: true, message: { t: "front" } };
    case "result": {
      if (!isNonEmptyString(value.id)) return fail("result: id missing");
      if (value.result === undefined) return fail("result: result missing");
      return { ok: true, message: { t: "result", id: value.id, result: value.result } };
    }
    default:
      return fail(`unknown message type ${String(value.t)}`);
  }
}

/** Messages arriving at an app frame from the relay. */
export function parseRelayMessage(raw: string): ParseResult<RelayMessage> {
  const decoded = decode(raw);
  if (!decoded.ok) return decoded;
  const value = decoded.message;
  switch (tagOf(value)) {
    case "welcome": {
      if (!versionOk(value)) return fail(`protocol version ${String(value.v)} is not supported`);
      // A welcome is the relay reporting a VERIFIED identity. One naming nobody
      // could only come from a relay that admitted a socket without deciding
      // whose it was, and that state is what v3 exists to make unreachable.
      if (!isNonEmptyString(value.userId)) return fail("welcome: userId missing");
      const userName = value.userName ?? null;
      if (userName !== null && typeof userName !== "string") {
        return fail("welcome: userName is not a string");
      }
      const workspaces = parseListings(value.workspaces);
      if (workspaces === false) return fail("welcome: workspaces malformed");
      const projects = parseListings(value.projects);
      if (projects === false) return fail("welcome: projects malformed");
      return {
        ok: true,
        message: {
          t: "welcome",
          v: WIRE_PROTOCOL_VERSION,
          userId: value.userId,
          userName,
          workspaces,
          projects,
        },
      };
    }
    case "call": {
      if (!isNonEmptyString(value.id)) return fail("call: id missing");
      if (!isNonEmptyString(value.frameId)) return fail("call: frameId missing");
      if (!isNonEmptyString(value.tool)) return fail("call: tool missing");
      if (!isJsonObject(value.args)) return fail("call: args missing");
      if (typeof value.budgetMs !== "number") return fail("call: budgetMs missing");
      return {
        ok: true,
        message: {
          t: "call",
          id: value.id,
          frameId: value.frameId,
          tool: value.tool,
          args: value.args,
          budgetMs: value.budgetMs,
        },
      };
    }
    case "abandon": {
      if (!isNonEmptyString(value.id)) return fail("abandon: id missing");
      return {
        ok: true,
        message: {
          t: "abandon",
          id: value.id,
          reason: typeof value.reason === "string" ? value.reason : "unstated",
        },
      };
    }
    case "session": {
      const fields = parseSessionFields(value);
      if (fields === null) return fail("session: state missing or incoherent");
      return { ok: true, message: { t: "session", ...fields } };
    }
    default:
      return fail(`unknown message type ${String(value.t)}`);
  }
}

/**
 * The most a report may weigh. The largest real one is a refusal carrying a
 * full close reason, well under this; anything bigger is not a report, and it
 * is refused before `JSON.parse` spends anything on it.
 */
const MAX_REPORT_BYTES = 1024;

/** The WebSocket protocol's cap on a close reason, in bytes. */
const MAX_CLOSE_REASON = 123;

function isCount(value: JsonValue | undefined): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 1;
}

/**
 * A frame's drop report, read from an UNAUTHENTICATED request body.
 *
 * ⚠️ **The result is rebuilt field by field, never passed through.** Whatever
 * this returns goes straight into a log line, and a log line is the one place a
 * body's extra keys would survive unread — a report carrying a token would
 * land it in Cloudflare's logs. The parser is the allowlist.
 */
export function parseDropReport(raw: string): ParseResult<DropReport> {
  if (raw.length > MAX_REPORT_BYTES) return fail("drop_report: body too large");
  const decoded = decode(raw);
  if (!decoded.ok) return decoded;
  const message = decoded.message;
  if (tagOf(message) !== "drop_report") return fail("drop_report: wrong message type");
  if (!versionOk(message)) return fail(`protocol version ${String(message.v)} is not supported`);
  if (typeof message.ref !== "string" || !isPrincipalRef(message.ref)) {
    return fail("drop_report: malformed ref");
  }
  if (!isCount(message.consecutive)) return fail("drop_report: consecutive is not a count");
  if (!isJsonObject(message.drop)) return fail("drop_report: drop is not an object");

  const drop = parseDrop(message.drop);
  if (!drop.ok) return drop;
  return {
    ok: true,
    message: {
      t: "drop_report",
      v: WIRE_PROTOCOL_VERSION,
      ref: message.ref,
      consecutive: message.consecutive,
      drop: drop.message,
    },
  };
}

function parseDrop(drop: JsonObject): ParseResult<SocketDrop> {
  switch (drop.kind) {
    case "refused":
      if (typeof drop.reason !== "string" || drop.reason.length > MAX_CLOSE_REASON) {
        return fail("drop_report: refusal reason is not a close reason");
      }
      return { ok: true, message: { kind: "refused", reason: drop.reason } };
    case "unreachable":
      if (!isCount(drop.attempts)) return fail("drop_report: attempts is not a count");
      return { ok: true, message: { kind: "unreachable", attempts: drop.attempts } };
    case "dropped": {
      const code = drop.code;
      if (code !== null && !(typeof code === "number" && Number.isInteger(code))) {
        return fail("drop_report: close code is not an integer");
      }
      return { ok: true, message: { kind: "dropped", code } };
    }
    default:
      return fail("drop_report: unknown drop kind");
  }
}
